import { useEffect, useState } from 'react';
import { FeatureValueByPGListParam, getFeatureValueByPGList } from '@services/filter/ProductGroupFeatureValue.service';
import { ProductGroupFeatureValue, ProductGroupPriceBandValue } from '@utils/pageConfig/Types';
import { getAbortController } from '@services/utils/OverviewServiceUtils';
import { usePrevious } from '../common/CommonHooks';
import { isEqual } from 'lodash';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getProductGroupsPriceBand, PricebandByPGListParam } from '@services/ProductGroup.service';
import { ProductType } from '@pages/product-analyzer/Product';

/**
 * useEffect to call FeatureValueByPGList service API, when there is a change in the param input
 * @param param side filters input
 */
export const useFetchProductGroupFeatureValue = (param: FeatureValueByPGListParam) => {
  const pagesConfig = usePageConfig();
  const [data, setData] = useState<ProductGroupFeatureValue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [controller] = useState<AbortController>(getAbortController());

  const callFeatureValueByPGList = () => {
    setLoading(true);

    getFeatureValueByPGList(param, controller).then((response) => {
      // set response to pages config redux
      pagesConfig.setProductGroupFeatureValueList(response);
      setData(response);
      setLoading(false);
    });
  };

  // get param's previous state value
  const previousState = usePrevious(param);

  // Applicable when country is changed
  useEffect(() => {
    // check whether parameter contains any undefined value and whether if it's same with previous state value
    if (!isAllEmpty(param) && previousState && !isEqual(previousState, param)) {
      callFeatureValueByPGList();
    }
  }, [pagesConfig.pgsByAppliedCountry, pagesConfig.selectedTimeFrame]);

  return { data, loading };
};

export const useFetchProductGroupPriceBandValueList = (param: PricebandByPGListParam) => {
  const pagesConfig = usePageConfig();
  const [data, setData] = useState<ProductGroupPriceBandValue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const callFeatureValueByPGList = async () => {
    setLoading(true);
    await Promise.all(
      param.country.map(async (countryCode) => {
        const response = await getProductGroupsPriceBand(param.clientId, [countryCode]);

        // set response to pages config redux
        const productGroupPriceBandList = pagesConfig.productGroupPriceBandValueList;
        response.forEach((responseData) => productGroupPriceBandList.push(responseData));
        pagesConfig.setProductGroupPriceBandValueList(productGroupPriceBandList);
        setData(productGroupPriceBandList);
      }),
    ).then(() => {
      setLoading(false);
    });
  };

  // get param's previous state value
  const previousState = usePrevious(param);

  // Applicable when country is changed
  useEffect(() => {
    // check whether parameter contains any undefined value and whether if it's same with previous state value
    if (
      previousState &&
      !isEqual(previousState, param) &&
      isAllowSubsciptionForPriceband(param.productType) &&
      param.clientId !== 1
    ) {
      callFeatureValueByPGList();
    }
  }, [pagesConfig.savedFilterCountryList, pagesConfig.pageType, param]);

  return { data, loading };
};

/**
 * check for every value in the parameter whether if it's undefined
 * additional checking for country and product group since both are array
 * @param param Product Group Feature Value request parameter
 * return False if every value within the object contains value
 */
const isAllEmpty = (param: FeatureValueByPGListParam) => {
  if (!param) return true;

  if (Object.values(param).every((value) => value === undefined)) return true;

  return param.country.length === 0 || param.productGroup.length === 0;
};

const subscriptionAllowedForPricebandApi = [ProductType.PerformanceAnalyzer, ProductType.RetailKad];

const isAllowSubsciptionForPriceband = (productType: ProductType[]) => {
  return subscriptionAllowedForPricebandApi.some((allowPt) => productType.includes(allowPt));
};
